import { PlcAdapter } from "stores/plc/Adapter"
import { ChannelSlug } from "stores/scheduler/enum"


/**
 * Contiene la configurazione dei COMPONENTS di una FARMS
 */
export interface SharedServices {

    // URL per chiamate HTTP dove recuperare i METADATA
    host?: {
        url: string
    }

    // Dati per la connessione alla coda NATS in EDGE
    nats?: Nats

    // Dati per la connessione alla coda NATS in CLOUD
    cloud_nats?: Nats

    /** opzioni per la visualizzazione dei COMPONENTS*/
    layout?: {
        /** templates per l'impaginazione dei COMPONENTS 
         * i COMPONENT hano "group" che matcha con "name" e determina queale template applicare (p.e. titolo da far uscire)
        */
        templates?: {
            /** nome identificativo del template */
            name: string,
            /** stringa che deve apparire a shermo */
            title?: string,
            /** gruppo ruuvi che concorrono alla media della temperatura/umidità (tags) */
            ruuvi?: string,
            /** se true indica che il gruppo deve essere aggregato a quello precedente */
            group?: boolean,
        }[],
    }

    /** tutti i componenti della FARM */
    components: Component[]

    /** Indica le voci che questa FARM espone nel MAIN MENU (dentro GROW-UNIT)  */
    guMenuItems?: {
        /** codice identificativo della voce */
        code?: "lighting-ps" | "recipe" | "map-average" | "climate" | null
        /** se è invisibile o no */
        hidden?: boolean
    }[]
}

/**
 * Sono i dati per potersi collegare ad una coda NATS
 */
export interface Nats {
    /** se è un array di host, si connette al primo disponibile */
    host?: string | string[]
    /** porta di connessione */
    port?: number
    /** username per l'autenticazione */
    username?: string // skip verification if null or empty
    /** password per l'autenticazione */
    password?: string
    /** se true abilita la connessione TLS */
    enable_tls?: boolean
    /** ??? */
    response_timeout?: number
    /** topic di sottoscrizione */
    subscribe: string
    /** topic per la pubblicazione */
    publish: string
}

/**
 * Un COMPONENT è un elemento della FARM che può essere visualizzato nel FE
 * contiene i dati per la corretta visualizzazione
 * I COMPONENTS sono raggruppati in base al loro tipo (type:COMPONENT_TYPE)
 */
export interface Component {
    /** codice identificativo DEL COMPONENT */
    code?: string
    /** nome visualizzato sul FE */
    name?: string
    /** il super-type a cui appartiene questo 
     *  definisce anche dove collocare questo COMPONENT nella lista dei COMPONENTS del FE
     * fa riferimento anche al parametro "templates" di "layout" in "SharedServices"
     * per nn mettere nessun titolo ma raggruppare i componenti in verticaleusare il carattere '|'
     */
    group?: string | string[],
    /** tags utilizzati per individuare il COMPONENT su description di un METADATA */
    tags?: string[]
    /** tipo di COMPONENT */
    type: COMPONENT_TYPE
    /** indica se il COMPONENT (true=) è disabilitato.
     * Se è disabilitato non sara' "cliccabile" nella lista del FE. 
     * Opzionale. Default=false */
    disabled?: boolean
    /** indica se il COMPONENT (true=) NON è presente nella lista del FE. 
        Opzionale. Default=false */
    hidden?: boolean
    /** SUBJECTS NATS del COMPONENT */
    subjects?: string[]
    /** opzioni specifiche del COMPONENT */
    options?: OptionsBase
    /** schema di interfacciamento con il PLC */
    adapter?: PlcAdapter,
    /** 
     * link (al "code") ad altri SUB-COMPONENTS 
     * per esempio:
     * { "climate": "CLI001", "lighting": "LIG001" }
     */
    links?: { [name: string]: string }
}

/**
 * Interfaccia base delle le "options"". 
 * Le "options" permettono di avere proprietà SPECIFICHE per un COMPONENT
 */
export interface OptionsBase {
    grafana?: GrafanaOption[]
    compact?: boolean,
}

/**
 * Opzioni per collegare un punto del COMPONENT ad un grafico GRAFANA
 */
type GrafanaOption = {
    /** codice del punto sul FE dove appare il bottone di apertura */
    code: string,
    /** titolo sul DRAWER */
    title?: string,
    /** URL del grafico che appare sul DRAWER */
    url: string
}

/** CMPONENT di tipo GROW-UNIT */
export interface GrowUnitComponent extends Component {
    type: COMPONENT_TYPE.GROWUNIT
    options: {
        /** codice della GROW-UNIT */
        code: string
        /** ACTIONS */
        actions?: {
            /** CARD REFILL */
            refill?: {
                /** [WEFRY, RONCADE]
                 * è editabile SOLO il MAIN TANK 
                 * */
                mainOnly?: boolean
            },
            /** CARD WASHING */
            washing?: {
                /** [RONCADE]
                 * indeica (true) che non c'e' la card WASHING nella sezione ACTIONS
                 */
                hidden?: boolean
            },
            /** CARD DRAINAGE */
            drainage?: {
                /** [WEFRY]
                 * - "timer": visualizza la CARD WASTE di tipo TIMER
                 * - "minilab": visualizza la CARD WASTE per i MINILAB
                 * - null [default]: non visualizza la CARD WASTE
                 */
                template?: "minilab" | "timer" | null
            },
            /** N1 COOLING */
            n1Cooling?: {
                /** se true visualizza la CARD N1 COOLING */
                visible?: boolean
            },
        }
        /** TANKS (PLANT)*/
        tanks?: {
            /** tipo di layout per i TANKS di irrigazione 
             * - "1:3-2" 
             * [WEGROW] 1 tank con 3 pompe di ingresso e 2 di scarico
             * Le pompe di scarico sono disattivabili
             * Nel sinottico IRRIGATION non ci sono le colonne: tutta LEFT MAX e PRESSURE OUTLET
             * - "0:1-0"
             * [RONCADE M-LABS] 0 tank con 1 pompa di ingresso e 0 di scarico
             * - "1:1-1"
             * [RONCADE F_LAB] 1 tank con 1 pompa di ingresso e 1 di scarico (con pressione e MODE)
             * - null 
             * [default] 6 tank con una valvola di ingresso e 1 di scarico 
             * */
            irrigationTanks?: "1:3-2" | "0:1-0" | "1:1-1" | null,

            /**
             * "default" prelevo gli alarm e visualizzo i levelli min e max
             * "only-max" visualizzo solo il max e non il min
             */
            irrigationAlertType?: null | "default" | "only-max"
            /**
             * se true inverte la logica dell'allarme di MAX LEVEL
             */
            irrigationAlertMaxInverse?: boolean
            /**
             * se true inverte la logica dell'allarme di MAX LEVEL
             */
            irrigationAlertMinInverse?: boolean

            /** 
             * se la valvola WASTE è cliccabile
             * "manual": c'e' il pulsante per attivare/disattivare il waste
             * null [default]: waste non è cliccabile
            */
            waste?: "manual" | null,
            /**
             * "filter": c'e' il componente FILTER
             * "check": c'e' il componente PRESS. CHECK
             */
            pressure?: "filter" | "check",
            /** 
             * Componente tra la MAIN PUMP e IRRIGATION  
             * "mix": solo valvola "MIX"  
             * "valve-mix": valvola aperto/chiuso irrigazione e valvola "MIX"  
             */
            irrigationValve?: "mix" | "valve-mix",
            /** 
             * @deprecated utilizzare "tanks"  
             * Sono i tanks ACID, BASE, A, B  
             * "manual": è possibile comandare i singoli TANKS dei nutrienti a mano  
             *  null [default]: i TANKS dei nutrienti sono comandati solo dalla ricetta  
             */
            nutrients?: "manual" | null,
            /**
             * non visualizzare l'alert di MAX-LEVEL
             */
            maxLevelAlertDisabled?: boolean
            /**
             * Non visualizza la pressione "INLET PRESSURE"
             */
            hideInletPressure?: boolean
            /**
             * @deprecated utilizzare "tanks"   
             * riguardo la MAIN PUMP
             */
            mainTank?: {
                /** temperatura */
                tempAlias?: string
            },
            mainPump?: {
                /** indica se ha o no lo SBLOCCO DEL'INVERTER */
                hasInverterUnlock?: boolean
            },

            tanks?: TankConfig[]

        }
        /** LAYERS */
        layers?: {
            // [II] bisognerebbe mettere "fans" qua dentro
            /**
             * template generaleindica se il componente è in modalità "lab"
             * - "lab": il componente è in modalità laboratorio
             * - null [default]: il componente è in modalità normale
             */
            template?: "lab" | null,
            /** 
             * indica che non devono  essere visibili le CARD del RECIPE (a sinistra nella sezione LAYERS)  
             * */
            hideRecipeCards?: boolean,
            /**
             * definizione del LAYERS
             * se definito: LIGHTING lo usa per default e IRRIGATION lo usa solo se isLayerDef=true
             * se NON definito sia LIGHTING che IRRIGATION usano i dati BE della GROW-UNIT
             */
            def?: LayerConfig[]
        },
        fans?: {
            /** nome che deve aparire per questo FAN */
            label?: string
            /**
             * LAYER PLC [SOLO PER LAB]
             * questa prop c'e' se il FAN è abbinato ad un LAYER
             * altrimenti è abbinato ad una colonna dipendentemente alla posizione nell'array fans
             * NOTE: al momento funziona SOLO per i LAYER con "template" = "lab"
             * */
            index?: number
            /** non visualizzare la box della velocità (non si puo' regolare) */
            hideSpeed?: boolean,
            /** numero di torri che gestisce questo FAN */
            span?: number
            /**
             * [TEMPLATE "towers"] COLUMNS servite da questo FAN (base 1, come LayerConfig.column)
             * se almeno un FAN della GROW-UNIT definisce "columns" la sezione LAYERS
             * viene renderizzata a griglia e la FanCard appare sopra alla zona di LAYERS servita,
             * estesa in orizzontale sulle COLUMNS indicate
             */
            columns?: number[]
            /**
             * [TEMPLATE "towers"] ROWS servite da questo FAN (base 1, come LayerConfig.row)
             * la FanCard appare subito sopra alla prima ROW servita
             * se non definito il FAN serve tutte le ROWS delle sue "columns"
             */
            rows?: number[]
            /** comando accensione spegnimento ventola
             * - false [default]
             * servono due alias: uno per leggere [INV_FAN_{index}_fQ_Run] e l'altro per scrivere [INV_FAN_{index}_f_Process_Run_RQY]
             * - true 
             * il PLC si aspetta una sola variabile in lettura/scrittura [fQ_FAN_{index}] */
            aliasRW?: boolean
        }[],
        /** ADJ */
        adj?: {
            /** 
             * Se visualizzare (true) o meno (false) schema dell'impianto (quello che appare in PLANT) 
             * */
            showPlant?: boolean,
            /**
             * Numero di PROBES da visualizzare (ogni PROBES è composto da sensore PH e EC)
             * default = 2 (quindi 2 PH e 2 EC)
             */
            numProbes?: number,
            /**
             * fa riferimanto ad una versione fittizia del ADJ PLC
             * "v2": 
             * - mostra THRESHOLD 0 in ADVANCED
             * - visualizza una differente TABLE per i PROBES
             */
            version?: "v1" | "v2" | null,
            /**
             * se nascondere (true) o meno (false) la CARD ALARMS
             */
            hideAlarms?: boolean
        },
        /** LIGHTING */
        lighting?: LightingOption,
        /** IRRIGATION */
        irrigation?: {

            /** 
             * indica che IRRIGATION ha i LAYERs definiti in layers.def (se ci sono)
             * e non nei dati BE della GROWUNIT 
             * */
            isLayerDef?: boolean,

            /**
             * Se true visualizzo nella tabella IRRIGATION solo la colonna PRES.OUTLET
             */
            onlyPresOutlet?: boolean,

            /**
             * se true nasconde la sezione LEVEL MAX nella tabella IRRIGATION
             */
            hideLevelMax?: boolean

        },
        /** Le opzioni dello SCHEDULER */
        scheduler?: SchedulerOption,
    } & OptionsBase
}

/**
 * Configurazione dello SCHEDULER
 */
export interface SchedulerOption {
    /** lista di SLUG utilizzati nel plc. Viene concatenato nel nome della variabile SCD_{SLUG}_ */
    types?: Partial<Record<ChannelSlug, {
        name: string,
        /** visualizza/nasconde l'intero SCHEDULER */
        hide?: boolean
        /** LABELS da usare per i valori dei SLOTS. usato per i CHANNELS del LIGHTING */
        labels?: { [code: string]: string },
        /** CHANNELS presenti */
        channels: {
            /** Nome che deve apparire nell'interfaccia */
            name: string,
            /** Indice da usare nella concatenazione SCD_{SLUG}_XXX_{INDEX}. Se non è presente usa quelli esposti dal PLC */
            index?: number,
            /** SUBJECT NATS da usare. se c'e' sovrascrive quello in root su questo oggetto */
            subject?: string,
            /** Se il CHANNEL è visibile o meno. Se settato sovrascrive il valore del PLC */
            hide?: boolean,
        }[],

    }>>,
    subject?: string,
}

/**
 * Configuraizone di un LAYER per i COMPONENTS LIGHTING e IRRIGATION
 */
export interface LightingOption {
    /** template per la visualizzazione della tabella LIGHTING 
     * null | "default": 0 = spento; 1 = acceso. step temporale di mezz'ora
     * "palette-16": 16 indici con 4 canali (white, red, blue, infrared) con uno ste temprale di un ora
    */
    template?: "default" | "palette-16" | null,
    /**
     * numero di PALETTE VALUES definite nel PLC
     */
    paletteLength?: number,
    /** service code (subject NATS) per la gestione i LIGHTING */
    subject?: string
}

/**
 * Configurazione per i TANKS: N1, ACID, BASE, SOL_A, SOL_B
 */
export interface TankConfig {
    /** nome univoco del TANK (N1, ACID, BASE, SOL_A, SOL_B ) */
    name?: string
    /** ATTENZIONE: funziona solo per N1
     * se deve uscire la temperatura e quindi l'alias da usare 
     * esempio: tempAlias: "TEMP_MIS"
     * */
    tempAlias?: string
    /** ATTENZIONE: non funziona per N1
    * Riferito alla valvola di scarico del TANK
    * "manual": è possibile comandare i singoli TANKS dei nutrienti a mano. 
    *          In pratica la VALVER OUT è cliccabile
    *  null [default]: i TANKS dei nutrienti sono comandati solo dalla ricetta
    */
    valveOutTemplate?: "manual" | null,

    /**
     * Se impostato sul TANK escono due bottoni per il settaggio manuale del livello minimo e massimo
     * sono due alias [min, max] 
     */
    levelSetMinMaxAlias?: [string, string],
}


/**
 * HVAC COMPONENT  
 * Un componente HVAC è un sistema di climatizzazione  
 * [CARD](..\pages\plc\main\subsystems\hvac\Pag.tsx)  
 * [MINICARD](..\pages\plc\services\components\hvac\HVACCmp.tsx)  
 */
export interface HVACComponent extends Component {
    type: COMPONENT_TYPE.HVAC
    options?: {
        /**
         * null: default 
         * `no-chiller`: non visualizza i chiller [RIAD, WEGROW]
         * `no-chiller-small` [RONCADE]
         */
        template?: "no-chiller" | "no-chiller-small" | null,
        /** 
         * SOLO TEMPLATE "no-chiller" 
         * se true non visualizza il valore "H2O PRESSURE"
         */
        h2oPressHide?: boolean,
        /** 
         * SOLO TEMPLATE "no-chiller" 
         * se true non visualizza il valore "SATURATION"
         */
        saturationHide?: boolean,
        /** 
         * nasconde la sezione RUUVI 
         * cioe' i campi: "PROBE MODE", "RUUVI TEMP." e "RUUVI UR"
         * */
        utaHideRuuvi?: boolean,
        /** 
         * nasconde PROBE MODE
         * */
        probeModeHide?: boolean,
        /** 
         * nasconde REMOTE CONTROL
         * */
        remoteControlHide?: boolean,

    } & OptionsBase
}

/**
 * CLIMATE COMPONENT  
 * Si occupa di settare lo SCHEDULING per la climatizzazione
 * [CARD](..\pages\plc\main\subsystems\hvac\Pag.tsx)  
 */
export interface ClimateComponent extends Component {
    type: COMPONENT_TYPE.CLIMATE
    options?: {
        bucket?: {
            /** nome del bucket NATS da utilizzare */
            name?: string,
            /** key del bucket NATS da utilizzare */
            key?: string,
            /** valore di default se non esiste nulla sul bucket */
            default?: any,
        }
    } & OptionsBase
}


/**
 * POWERS COMPONENT
 */
export interface PowersComponent extends Component {
    type: COMPONENT_TYPE.POWERS
}

/**
 * GERMINATOR COMPONENT
 */
export interface GerminatorComponent extends Component {
    type: COMPONENT_TYPE.GERMINATOR
    options?: {
        /**
         * - "tc:no-fans": [WEGROW] TEMPERATURE CONTROL | visualizza UTA FANS | nasconde FANS SPEED
         * - "small": [RONCADE] adatto ai minilab | 2 FANS | non visualizza TEMPERATURE CONTROL
         */
        template?: "tc:no-fans" | "small" | null
    } & OptionsBase
}

/**
 * OSMOTIC WATER COMPONENT
 */
export interface OsmoticWaterComponent extends Component {
    type: COMPONENT_TYPE.OSMOTIC_WATER
    options?: {
        /** 
         * template per la visualizzazione del componente
         * - "single-tank": c'e' un solo tank di osmosi inversa
         * - null [default]: visualizza i due tank di osmosi inversa
         */
        template?: "single-tank" | null
    } & OptionsBase
    /**
     * schema di interfacciamento con il PLC. Path opzionali:
     * - "outlet_counter": alias del contalitri in uscita.
     *   Se presente viene visualizzato "OUTLET COUNTER" vicino a "OUTLET PRESSURE"
     * @example
     * adapter: { children: { outlet_counter: "Outlet_LitersCounter_Total" } }
     */
    adapter?: PlcAdapter
}

/**
 * WATER TREATMENT COMPONENT
 */
export interface WaterTreatmentComponent extends Component {
    type: COMPONENT_TYPE.WATER_TREATMENT
    options?: {
        /** al momento non usato */
        template?: "minilab" | null
    } & OptionsBase
}

/**
 * CO2 COMPONENT
 */
export interface Co2Component extends Component {
    type: COMPONENT_TYPE.CO2
    options?: {
        /**
         * indica il tipo di template da usare per COMPONENT CO2
         */
        template?: null | "standard" | "minimal"


        //#region STANDARD

        /** nasconde il parametro TANK PRESSURE */
        hidePressure?: boolean
        /** visualizza il parametro TANK VOLUME */
        showVolume?: boolean
        /** visualizza il parametro CO2 MASS */
        showMass?: boolean

        //#endregion
    } & OptionsBase
}

/**
 * CO2 PANEL COMPONENT
 */
export interface Co2PanelComponent extends Component {
    type: COMPONENT_TYPE.CO2_PANEL
    options?: {
        /** STANZE servite dal pannello: una riga nella card ROOMS */
        rooms?: Co2PanelRoom[]
    } & OptionsBase
}

/**
 * STANZA di un CO2 PANEL
 */
export interface Co2PanelRoom {
    /** nome visualizzato (es. "LAB 3") */
    name: string
    /** prefisso PLC che sostituisce CO200X negli alias (es. "CO2002") */
    code: string
}

/**
 * THERMAL POWER PLANT
 * Permette di leggere/scrivere i settaggi delle pompe per il riscaldamento e il raffreddamento e di visualizzare i dati di temperatura in ingresso e uscita
 * [CARD](..\pages\plc\main\subsystems\thermalPowerPLant)  
 * [MINICARD](..\pages\plc\services\components\thermo)  

 */
export interface ThermalPowerPlantComponent extends Component {
    type: COMPONENT_TYPE.THERMAL_POWER_PLANT
    options?: {
        /**
         * indica il tipo di template da usare
         * - (default) [RONCADE] "2_pump_4_count": ci sono due pompe e 4 contabilizzatori 
         * - [PORDENONE] "3_pump_2_count": ci sono tre pompe e 2 contabilizzatori
         */
        template?: null | "2_pump_4_count" | "3_pump_2_count"
        /**
         * nasconde i parametri del circuito 2 e lascia solo l'1
         */
        hideCircuit2?: boolean

    } & OptionsBase
}


/**
 * Tipi di COMPONENTS
 */
export enum COMPONENT_TYPE {
    GERMINATOR = "germinator",
    GROWUNIT = "grow-unit",
    HVAC = "hvac",
    POWERS = "powers",
    OSMOTIC_WATER = "osmotic-water",
    SERVICES = "services",
    CO2 = "co2",
    CO2_PANEL = "co2-panel",
    WATER_TREATMENT = "water-treatment",
    SENSORS = "sensors",
    THERMAL_POWER_PLANT = "thermal-power-plant",
    CLIMATE = "climate",
}



/**
 * Parametri di configurazione di un LAYER
 * utilizzati al posto dei dati BE della GROWUNIT
 */
export type LayerConfig = {
    /**
     * LAYER PLC: è un numero progressivo base 1 a livello di GROW-UNIT e segue la logica del PLC.
     * Nei def di FARM-CONFIG può essere un indice a livello di FARM (base 0) quando il subject è condiviso,
     * per esempio LAB01 e LAB02 di RONCADE
     */
    index?: number;
    /**
     * @deprecated usare index
     */
    indexFarm?: number;
    /** colonna a cui appartiene questo layer */
    column: number;
    /** riga a cui appartiene questo layer */
    row: number;
    /** nome del LAYER */
    name?: string;
    /** nome della colonna */
    columnName?: string;
};
